import React, {useEffect, useState} from 'react';
import {Platform, Pressable, ScrollView, View} from 'react-native';
import CCText from '@/Atoms/CCText';
import CCLoader from '@/Atoms/CCLoader';
import {
  useClubDetail,
  useClubPromotion,
  useMarkPromotionSeen,
  useClubActiveEvent,
  useCreateEvent,
  useEndEvent,
} from '@/Hooks/ClubsApi/useClubsQueries';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';
import ChatTab from './components/ChatTab';
import LeaderboardTab from './components/LeaderboardTab';
import MembersTab from './components/MembersTab';
import InviteShare from './components/InviteShare';
import ClubBadge from './components/ClubBadge';
import PromotionModal from './components/PromotionModal';
import EventBar from './components/EventBar';
import CreateEventSheet from './components/CreateEventSheet';
import {ClubRoomSkeleton} from './components/ClubRoomSkeletons';
import {BADGES} from './badgeOptions';
import type {PieceKey} from './clubPieces';
import useLoggedInUserStore from '@/Store/loggedInUserstore';

const TABS = ['chat', 'leaderboard', 'members'] as const;
const pieceFor = (badgeId?: string): PieceKey =>
  (BADGES.find(b => b.id === badgeId)?.piece ?? 'pawn') as PieceKey;

export default function ClubRoom({navigation, route}: any) {
  const {clubId} = route?.params ?? {};
  const {data, isLoading} = useClubDetail(clubId);
  const [tab, setTab] = useState<(typeof TABS)[number]>('chat');
  const [inviteOpen, setInviteOpen] = useState(false);
  const {isMobileOrTab} = useMediaQuery();
  const isWide = !isMobileOrTab;
  const isStaff = useLoggedInUserStore(state => !!state.userData?.is_staff);

  const goToClubs = () => navigation.navigate('Clubs');

  const openStandings = () => navigation.navigate('ClubStandings', {clubId});

  // Promotion celebration — shown once when the club climbed a division.
  const {data: promoData} = useClubPromotion(clubId);
  const markSeen = useMarkPromotionSeen(clubId);
  const [promoDismissed, setPromoDismissed] = useState(false);
  const promotion = promoData?.promotion ?? null;
  const dismissPromotion = () => {
    setPromoDismissed(true);
    markSeen.mutate();
  };

  // Puzzle Storm event — pinned at the top of the room when active.
  const {data: eventData} = useClubActiveEvent(clubId);
  const createEvent = useCreateEvent(clubId);
  const endEvent = useEndEvent(clubId);
  const [eventSheetOpen, setEventSheetOpen] = useState(false);
  const activeEvent = eventData?.event ?? null;

  // Non-active clubs (disabled/banned/pending_review/archived) must not open the
  // room — redirect to the profile, which shows the status banner + raise-a-ticket
  // note. Effect (not render-time) so navigation happens as a side effect.
  const clubStatus = data?.club?.status;
  const isClubActive = (clubStatus ?? 'active') === 'active';
  useEffect(() => {
    if (data && !isClubActive) {
      navigation.navigate('ClubProfile', {clubId});
    }
  }, [data, isClubActive, clubId, navigation]);

  if (isLoading || !data) {
    return <ClubRoomSkeleton isWide={isWide} />;
  }
  if (!isClubActive) {
    // While the redirect effect runs, don't render the room.
    return <CCLoader loading={true} />;
  }
  const club = data.club;
  const isAdmin = club.my_role === 'admin';
  const canManage = isAdmin || isStaff;
  const memberLabel = `${club.member_count} member${
    club.member_count !== 1 ? 's' : ''
  }`;

  return (
    <View
      testID="club-room-root"
      style={[
        {flex: 1, backgroundColor: '#FAF6EB'},
        // ClubRoom is hosted in a fixed-height View by Application, so the room
        // can own the available viewport and keep the composer at the bottom.
        // Scrolling stays inside the messages and right rail.
        isWide && Platform.OS === 'web'
          ? ({
              height: '100%',
              minHeight: 0,
              overflow: 'hidden',
            } as any)
          : null,
      ]}>
      {promotion && !promoDismissed ? (
        <PromotionModal promotion={promotion} onDismiss={dismissPromotion} />
      ) : null}
      {/* Header bar */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingHorizontal: isWide ? 28 : 14,
          paddingTop: isWide ? 20 : 8,
          paddingBottom: isWide ? 16 : 10,
          borderBottomWidth: 1,
          borderBottomColor: 'rgba(38,35,34,0.10)',
        }}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            flex: 1,
            gap: isWide ? 14 : 0,
          }}>
          {/* Back button — mobile shows a compact chevron; desktop shows a
              "‹ Clubs" pill so the wide layout also has a way back. */}
          {isWide ? (
            <Pressable
              testID="club-room-back"
              onPress={goToClubs}
              style={({pressed}) => [
                {
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 4,
                  paddingHorizontal: 12,
                  paddingVertical: 8,
                  borderRadius: 20,
                  borderWidth: 1,
                  borderColor: 'rgba(38,35,34,0.20)',
                  backgroundColor: '#fff',
                  marginRight: 4,
                },
                pressed && {opacity: 0.7},
              ]}>
              <CCText style={{fontSize: 16, lineHeight: 16, color: '#4D3F37'}}>
                ‹
              </CCText>
              <CCText
                style={{fontSize: 13.5, fontWeight: '600', color: '#4D3F37'}}>
                Clubs
              </CCText>
            </Pressable>
          ) : (
            <Pressable
              testID="club-room-back"
              onPress={goToClubs}
              style={{
                paddingHorizontal: 4,
                paddingVertical: 6,
                marginRight: 4,
              }}>
              <CCText style={{fontSize: 22, lineHeight: 22, color: '#4D3F37'}}>
                ‹
              </CCText>
            </Pressable>
          )}
          {/* Club badge crest */}
          <View style={{marginRight: isWide ? 0 : 10}}>
            <ClubBadge
              piece={pieceFor(club.badge_id)}
              color={club.background_color || '#3DAB9E'}
              size={isWide ? 48 : 38}
              radius={isWide ? 13 : 11}
            />
          </View>
          <View style={{flex: 1, minWidth: 0}}>
            {isWide ? (
              <CCText
                fontWeight="thunder"
                style={{fontSize: 24, color: '#262322'}}>
                {club.name}
              </CCText>
            ) : (
              <CCText
                style={{
                  fontSize: 16,
                  fontWeight: '600',
                  color: '#262322',
                  lineHeight: 18,
                }}>
                {club.name}
              </CCText>
            )}
            <CCText
              style={{
                fontSize: isWide ? 12.5 : 11.5,
                color: '#888280',
                marginTop: 2,
              }}>
              {memberLabel}
            </CCText>
          </View>
        </View>
        {/* Invite button (design: shown on both; mobile uses a compact pill) */}
        <Pressable
          testID={isWide ? 'club-invite-btn-wide' : 'club-invite-btn'}
          onPress={() => setInviteOpen(true)}
          style={{
            backgroundColor: '#3DAB9E',
            borderRadius: isWide ? 22 : 20,
            paddingHorizontal: isWide ? 18 : 13,
            paddingVertical: isWide ? 10 : 8,
            marginLeft: 8,
          }}>
          <CCText
            style={{
              color: '#fff',
              fontWeight: '600',
              fontSize: isWide ? 14 : 12.5,
            }}>
            Invite
          </CCText>
        </Pressable>
        {canManage ? (
          <Pressable
            testID="club-manage-btn"
            style={{
              backgroundColor: '#fff',
              borderWidth: 1,
              borderColor: 'rgba(38,35,34,0.20)',
              paddingHorizontal: isWide ? 16 : 12,
              paddingVertical: isWide ? 10 : 8,
              borderRadius: isWide ? 22 : 20,
              marginLeft: 8,
            }}
            onPress={() => navigation.navigate('ClubAdmin', {clubId})}>
            <CCText
              style={{
                color: '#4D3F37',
                fontWeight: '600',
                fontSize: isWide ? 14 : 12.5,
              }}>
              Manage
            </CCText>
          </Pressable>
        ) : null}
      </View>

      {/* Announcement banner */}
      {club.announcement ? (
        <View
          style={{
            backgroundColor: '#FFF0C8',
            borderRadius: 12,
            padding: isWide ? 13 : 12,
            marginHorizontal: isWide ? 28 : 14,
            marginTop: isWide ? 14 : 10,
            marginBottom: isWide ? 0 : 0,
          }}>
          <View style={{flexDirection: 'row', alignItems: 'flex-start'}}>
            <CCText
              style={{
                fontSize: isWide ? 16 : 15,
                marginRight: isWide ? 10 : 9,
              }}>
              📌
            </CCText>
            <View style={{flex: 1}}>
              <CCText
                style={{
                  fontSize: 10.5,
                  fontWeight: '700',
                  color: '#B08A22',
                  textTransform: 'uppercase',
                  letterSpacing: 0.06,
                  marginBottom: 1,
                }}>
                {isWide ? 'Pinned announcement' : 'Pinned'}
              </CCText>
              <CCText
                style={{
                  fontSize: isWide ? 13.5 : 13,
                  color: '#806234',
                  lineHeight: 18,
                }}>
                {club.announcement.text}
              </CCText>
            </View>
          </View>
        </View>
      ) : null}

      {/* Puzzle Storm event bar — pinned below the header when an event is active. */}
      {activeEvent ? (
        <EventBar
          event={activeEvent}
          isAdmin={isAdmin}
          onEnd={() => endEvent.mutate(activeEvent.id)}
        />
      ) : isAdmin ? (
        <Pressable
          testID="event-create-open"
          onPress={() => setEventSheetOpen(true)}
          style={{
            marginHorizontal: 14,
            marginTop: 10,
            borderRadius: 12,
            borderWidth: 1,
            borderColor: '#F3D08A',
            borderStyle: 'dashed',
            paddingVertical: 10,
            alignItems: 'center',
          }}>
          <CCText style={{fontSize: 12.5, fontWeight: '600', color: '#B0762A'}}>
            ⚡ Start a Puzzle Storm event
          </CCText>
        </Pressable>
      ) : null}
      <CreateEventSheet
        visible={eventSheetOpen}
        submitting={(createEvent as any).isLoading}
        onCancel={() => setEventSheetOpen(false)}
        onCreate={(title, goal, startsAt, endsAt) =>
          (createEvent as any).mutate(
            {title, goal, starts_at: startsAt, ends_at: endsAt},
            {onSuccess: () => setEventSheetOpen(false)},
          )
        }
      />

      {/* Invite bottom-sheet (opened from the Invite button) */}
      <InviteShare
        clubId={clubId}
        clubName={club.name}
        visibility={club.visibility}
        joinPolicy={club.join_policy}
        minRating={club.min_rating}
        visible={inviteOpen}
        onClose={() => setInviteOpen(false)}
      />

      {isWide ? (
        // ===== Desktop two-pane: chat card + right rail =====
        // The pane fills the remaining viewport height and never overflows; the
        // page itself doesn't scroll. Scrolling lives inside the chat message
        // list and inside each rail card.
        <View
          style={{
            flex: 1,
            flexDirection: 'row',
            minHeight: 0,
            overflow: 'hidden',
            paddingTop: 16,
            paddingHorizontal: 28,
            paddingBottom: 0,
            gap: 20,
            // Fill all remaining height below the header + optional bars, down to the
            // viewport bottom. The chat card and rail flex to fill this, so they
            // self-adjust to whichever slots (announcement / event bar) are shown.
          }}>
          {/* Chat card — fills full height; its inner FlatList scrolls, composer
              stays pinned to the bottom (design line 869). minHeight:0 lets the
              inner list shrink/scroll instead of stretching the column. */}
          <View
            testID="club-chat-tab"
            style={{
              flex: 2,
              minHeight: 0,
              alignSelf: 'stretch',
              backgroundColor: '#fff',
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              borderWidth: 1,
              borderBottomWidth: 0,
              borderColor: 'rgba(38,35,34,0.10)',
              overflow: 'hidden',
            }}>
            <ChatTab
              clubId={clubId}
              isWide
              isAdmin={isAdmin}
              navigation={navigation}
            />
          </View>
          {/* Right rail — the WHOLE rail scrolls as one column. The leaderboard card
              sizes to ~10 rows (scrolls inside beyond that); the members card sits
              below it, peeking into view so it's clearly scrollable to. */}
          <ScrollView
            style={{flex: 1, minHeight: 0, alignSelf: 'stretch'}}
            contentContainerStyle={{gap: 16, paddingBottom: 8}}
            showsVerticalScrollIndicator={true}>
            {/* Leaderboard card — sizes to content (LeaderboardTab caps + scrolls). */}
            <View
              style={{
                backgroundColor: '#fff',
                borderRadius: 14,
                padding: 16,
                borderWidth: 1,
                borderColor: 'rgba(38,35,34,0.10)',
                shadowColor: '#4D3F37',
                shadowOffset: {width: 0, height: 1},
                shadowOpacity: 0.05,
                shadowRadius: 2,
                overflow: 'hidden',
              }}>
              <LeaderboardTab
                clubId={clubId}
                variant="rail"
                onOpenStandings={openStandings}
              />
            </View>
            {/* Members card — fixed title, scrollable list inside. */}
            <View
              style={{
                backgroundColor: '#fff',
                borderRadius: 14,
                padding: 16,
                borderWidth: 1,
                borderColor: 'rgba(38,35,34,0.10)',
                shadowColor: '#4D3F37',
                shadowOffset: {width: 0, height: 1},
                shadowOpacity: 0.05,
                shadowRadius: 2,
                overflow: 'hidden',
              }}>
              <CCText
                style={{
                  fontSize: 13,
                  fontWeight: '700',
                  color: '#262322',
                  marginBottom: 10,
                }}>
                Members · {memberLabel}
              </CCText>
              <MembersTab members={club.members ?? []} variant="rail" />
            </View>
          </ScrollView>
        </View>
      ) : (
        // ===== Mobile: tab bar + single tab body =====
        <>
          <View
            style={{
              flexDirection: 'row',
              gap: 6,
              borderBottomWidth: 1,
              borderBottomColor: 'rgba(38,35,34,0.10)',
              paddingHorizontal: 14,
              paddingTop: 12,
            }}>
            {TABS.map(t => {
              const active = tab === t;
              return (
                <Pressable
                  key={t}
                  testID={`club-tab-${t}`}
                  onPress={() => setTab(t)}
                  style={{
                    paddingBottom: 13,
                    paddingTop: 2,
                    paddingHorizontal: 4,
                    alignItems: 'center',
                    borderBottomWidth: 2.5,
                    borderBottomColor: active ? '#3DAB9E' : 'transparent',
                    marginBottom: -1,
                  }}>
                  <CCText
                    style={{
                      fontWeight: '600',
                      color: active ? '#262322' : '#888280',
                      fontSize: 15,
                    }}>
                    {t[0].toUpperCase() + t.slice(1)}
                  </CCText>
                </Pressable>
              );
            })}
          </View>
          {tab === 'chat' ? (
            <View testID="club-chat-tab" style={{flex: 1}}>
              <ChatTab
                clubId={clubId}
                isAdmin={isAdmin}
                navigation={navigation}
              />
            </View>
          ) : tab === 'leaderboard' ? (
            <LeaderboardTab clubId={clubId} onOpenStandings={openStandings} />
          ) : (
            <MembersTab members={club.members ?? []} />
          )}
        </>
      )}
    </View>
  );
}
